import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '../../components/shared/Breadcrumb';

const PAGE_SIZES = [10, 50, 100, 500];
const initialForm = { email: '', nombre: '', password: '', role_slug: 'cliente', enabled: true };

function headers() {
  const token = sessionStorage.getItem('syrakis_token');
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function request(url, options = {}) {
  const response = await fetch(url, { credentials: 'same-origin', ...options, headers: { ...headers(), ...(options.headers || {}) } });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || data.error || 'No se pudo completar la operación.');
  return data;
}

function saveDownload(content, filename, type) {
  const blob = content instanceof Blob ? content : new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function exportRows(users) {
  return users.map((user) => ({
    ID: user.id,
    Nombre: user.nombre || 'Sin nombre',
    Email: user.email || '',
    Rol: user.role_name || user.role_slug || '',
    Estado: user.enabled ? 'Activo' : 'Inactivo',
    Creado: user.created_at || '',
  }));
}

function escapeCsv(value) {
  const text = String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadCsv(users) {
  const rows = exportRows(users);
  const columns = Object.keys(rows[0] || { ID: '', Nombre: '', Email: '', Rol: '', Estado: '', Creado: '' });
  const csv = [columns.join(','), ...rows.map((row) => columns.map((column) => escapeCsv(row[column])).join(','))].join('\n');
  saveDownload(`\ufeff${csv}`, 'usuarios.csv', 'text/csv;charset=utf-8;');
}

function downloadExcel(users) {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(exportRows(users));
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Usuarios');
  XLSX.writeFile(workbook, 'usuarios.xlsx');
}

function escapePdfText(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x20-\x7E]/g, '?')
    .replace(/[\\()]/g, '\\$&')
    .slice(0, 125);
}

function buildPdf(users) {
  const rows = exportRows(users);
  const lines = [
    `Listado de usuarios - ${new Date().toLocaleDateString('es-CL')}`,
    'ID | Nombre | Email | Rol | Estado',
    ...rows.map((row) => `${row.ID} | ${row.Nombre} | ${row.Email} | ${row.Rol} | ${row.Estado}`),
  ];
  const chunks = [];
  for (let index = 0; index < lines.length; index += 48) chunks.push(lines.slice(index, index + 48));

  const firstPageObject = 3;
  const fontObject = firstPageObject + chunks.length * 2;
  const objects = [];
  objects[1] = '<< /Type /Catalog /Pages 2 0 R >>';
  objects[2] = `<< /Type /Pages /Kids [${chunks.map((_, index) => `${firstPageObject + index * 2} 0 R`).join(' ')}] /Count ${chunks.length} >>`;

  chunks.forEach((chunk, index) => {
    const pageObject = firstPageObject + index * 2;
    const contentObject = pageObject + 1;
    const content = [
      'BT',
      '/F1 9 Tf',
      '40 760 Td',
      ...chunk.map((line, lineIndex) => `${lineIndex ? '0 -14 Td ' : ''}(${escapePdfText(line)}) Tj`),
      'ET',
    ].join('\n');
    objects[pageObject] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 ${fontObject} 0 R >> >> /Contents ${contentObject} 0 R >>`;
    objects[contentObject] = `<< /Length ${content.length} >>\nstream\n${content}\nendstream`;
  });
  objects[fontObject] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>';

  let pdf = '%PDF-1.4\n';
  const offsets = [0];
  for (let index = 1; index <= fontObject; index += 1) {
    offsets[index] = pdf.length;
    pdf += `${index} 0 obj\n${objects[index]}\nendobj\n`;
  }
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${fontObject + 1}\n0000000000 65535 f \n`;
  for (let index = 1; index <= fontObject; index += 1) pdf += `${String(offsets[index]).padStart(10, '0')} 00000 n \n`;
  pdf += `trailer\n<< /Size ${fontObject + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return pdf;
}

function downloadPdf(users) {
  saveDownload(buildPdf(users), 'usuarios.pdf', 'application/pdf');
}

export default function UsersPage() {
  const { id } = useParams();
  const editing = Boolean(id);
  const location = useLocation();
  const creating = location.pathname.endsWith('/nuevo');
  const formOpen = creating || editing;
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [viewing, setViewing] = useState(null);

  const currentUser = useMemo(() => users.find((user) => Number(user.id) === Number(id)), [users, id]);
  const pageCount = Math.max(1, Math.ceil(users.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pagedUsers = useMemo(() => users.slice((currentPage - 1) * pageSize, currentPage * pageSize), [users, currentPage, pageSize]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [userData, roleData] = await Promise.all([request('/api/users?limit=500'), request('/api/users/roles')]);
      setUsers(userData.users || []);
      setRoles(roleData.roles || []);
    } catch (reason) {
      setError(reason.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [pageSize]);
  useEffect(() => {
    if (editing && currentUser) {
      setForm({ email: currentUser.email || '', nombre: currentUser.nombre || '', password: '', role_slug: currentUser.role_slug || 'cliente', enabled: Boolean(currentUser.enabled) });
    } else if (creating) {
      setForm(initialForm);
    }
  }, [creating, editing, currentUser]);

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      const data = await request(editing ? `/api/users/${id}` : '/api/users', { method: editing ? 'PUT' : 'POST', body: JSON.stringify(payload) });
      setMessage(data.message);
      if (!editing) setForm(initialForm);
      await load();
      navigate('/usuarios');
    } catch (reason) {
      setError(reason.message);
    } finally {
      setSaving(false);
    }
  }

  async function disable(user) {
    if (!window.confirm(`¿Desactivar el acceso de ${user.email}?`)) return;
    try {
      const data = await request(`/api/users/${user.id}`, { method: 'DELETE' });
      setMessage(data.message);
      await load();
    } catch (reason) {
      setError(reason.message);
    }
  }

  async function remove(user) {
    if (!window.confirm(`¿Eliminar permanentemente a ${user.email}? Esta acción no se puede deshacer.`)) return;
    try {
      const data = await request(`/api/users/${user.id}/permanent`, { method: 'DELETE' });
      setMessage(data.message);
      if (viewing?.id === user.id) setViewing(null);
      await load();
    } catch (reason) {
      setError(reason.message);
    }
  }

  return (
    <>
      <Breadcrumb title="Gestión de usuarios" />
      {error && <div className="alert alert-danger mb-20">{error}</div>}
      {message && <div className="alert alert-success mb-20">{message}</div>}
      <div className="row gy-4">
        <div className="col-12">
          <div className="card h-100">
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div><h6 className="mb-4">Usuarios operativos</h6><p className="text-secondary-light mb-0">Superadmins se administran desde su módulo separado.</p></div>
              <Link className="btn btn-primary btn-sm d-flex align-items-center gap-2" to="/usuarios/nuevo"><Icon icon="ic:baseline-plus" />Nuevo usuario</Link>
            </div>
            <div className="card-body p-0">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 px-24 py-16 border-bottom">
                <label className="d-flex align-items-center gap-2 text-sm mb-0">Mostrar
                  <select className="form-select form-select-sm w-auto" value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))} aria-label="Usuarios por página">
                    {PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
                  </select>
                  usuarios por página
                </label>
                <div className="d-flex align-items-center gap-2" aria-label="Exportar usuarios">
                  <span className="text-sm text-secondary-light me-2">Exportar</span>
                  <button type="button" className="btn btn-sm btn-outline-success" title="Exportar CSV" onClick={() => downloadCsv(users)}><Icon icon="solar:file-download-linear" /> <span className="d-none d-md-inline">CSV</span></button>
                  <button type="button" className="btn btn-sm btn-outline-primary" title="Exportar Excel" onClick={() => downloadExcel(users)}><Icon icon="solar:document-text-linear" /> <span className="d-none d-md-inline">Excel</span></button>
                  <button type="button" className="btn btn-sm btn-outline-danger" title="Exportar PDF" onClick={() => downloadPdf(users)}><Icon icon="solar:file-text-linear" /> <span className="d-none d-md-inline">PDF</span></button>
                </div>
              </div>
              {loading && <p className="p-24 mb-0">Cargando usuarios...</p>}
              {!loading && !error && <div className="table-responsive"><table className="table bordered-table mb-0 align-middle"><thead><tr><th>Usuario</th><th>Rol</th><th>Estado</th><th className="text-center">Acciones</th></tr></thead><tbody>
                {pagedUsers.map((user) => <tr key={user.id}><td><div className="fw-semibold">{user.nombre || 'Sin nombre'}</div><small className="text-secondary-light">{user.email}</small></td><td>{user.role_name || user.role_slug}</td><td><span className={`badge ${user.enabled ? 'bg-success-50 text-success-600' : 'bg-danger-50 text-danger-600'}`}>{user.enabled ? 'Activo' : 'Inactivo'}</span></td><td><div className="d-flex justify-content-center align-items-center gap-2"><button type="button" className="btn btn-sm btn-outline-secondary" title="Ver usuario" onClick={() => setViewing(user)}><Icon icon="majesticons:eye-line" /></button><Link className="btn btn-sm btn-outline-primary" title="Editar usuario" to={`/usuarios/${user.id}/editar`}><Icon icon="solar:pen-2-linear" /></Link><button type="button" className="btn btn-sm btn-outline-warning" title="Desactivar usuario" onClick={() => disable(user)} disabled={!user.enabled}><Icon icon="solar:close-circle-linear" /></button><button type="button" className="btn btn-sm btn-outline-danger" title="Eliminar usuario" onClick={() => remove(user)}><Icon icon="solar:trash-bin-trash-linear" /></button></div></td></tr>)}
                {!pagedUsers.length && <tr><td colSpan="4" className="text-center py-32">No hay usuarios operativos.</td></tr>}
              </tbody></table></div>}
              {!loading && !error && <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 px-24 py-16 border-top"><span className="text-sm text-secondary-light">Mostrando {users.length ? ((currentPage - 1) * pageSize) + 1 : 0}–{Math.min(currentPage * pageSize, users.length)} de {users.length}</span><div className="d-flex align-items-center gap-2"><button type="button" className="btn btn-sm btn-light" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={currentPage === 1}>Anterior</button><span className="text-sm">Página {currentPage} de {pageCount}</span><button type="button" className="btn btn-sm btn-light" onClick={() => setPage((value) => Math.min(pageCount, value + 1))} disabled={currentPage === pageCount}>Siguiente</button></div></div>}
            </div>
          </div>
        </div>
      </div>
      {formOpen && <div className="modal d-block" style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }} role="dialog" aria-modal="true" aria-label={editing ? 'Editar usuario' : 'Crear usuario'} onClick={() => navigate('/usuarios')}>
        <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(event) => event.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header"><h6 className="modal-title">{editing ? 'Editar usuario' : 'Crear usuario'}</h6><button type="button" className="btn-close" aria-label="Cerrar" onClick={() => navigate('/usuarios')} /></div>
            <div className="modal-body p-24">
              {editing && !currentUser && !loading && <div className="alert alert-warning">Usuario no encontrado o no visible.</div>}
              <form onSubmit={submit}>
                <div className="row gy-3">
                  <div className="col-md-6"><label className="form-label">Nombre</label><input className="form-control" value={form.nombre} onChange={(event) => update('nombre', event.target.value)} required /></div>
                  <div className="col-md-6"><label className="form-label">Email</label><input type="email" className="form-control" value={form.email} onChange={(event) => update('email', event.target.value)} required /></div>
                  <div className="col-md-6"><label className="form-label">Rol</label><select className="form-select" value={form.role_slug} onChange={(event) => update('role_slug', event.target.value)}>{roles.map((role) => <option key={role.slug} value={role.slug}>{role.nombre}</option>)}</select></div>
                  <div className="col-md-6"><label className="form-label">Contraseña {editing && '(opcional)'}</label><input type="password" minLength="8" className="form-control" value={form.password} onChange={(event) => update('password', event.target.value)} required={!editing} /></div>
                  {editing && <div className="col-12"><div className="form-check form-switch"><input className="form-check-input" type="checkbox" checked={form.enabled} onChange={(event) => update('enabled', event.target.checked)} /><label className="form-check-label">Cuenta activa</label></div></div>}
                </div>
                <div className="d-flex justify-content-end gap-2 mt-24"><button type="button" className="btn btn-light" onClick={() => navigate('/usuarios')}>Cancelar</button><button className="btn btn-primary" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button></div>
              </form>
            </div>
          </div>
        </div>
      </div>}
      {viewing && <div className="modal d-block" role="dialog" aria-modal="true" aria-label="Detalle de usuario" onClick={() => setViewing(null)}><div className="modal-dialog modal-dialog-centered" onClick={(event) => event.stopPropagation()}><div className="modal-content"><div className="modal-header"><h6 className="modal-title">Detalle de usuario</h6><button type="button" className="btn-close" aria-label="Cerrar" onClick={() => setViewing(null)} /></div><div className="modal-body"><dl className="row mb-0"><dt className="col-sm-4">ID</dt><dd className="col-sm-8">{viewing.id}</dd><dt className="col-sm-4">Nombre</dt><dd className="col-sm-8">{viewing.nombre || 'Sin nombre'}</dd><dt className="col-sm-4">Email</dt><dd className="col-sm-8">{viewing.email}</dd><dt className="col-sm-4">Rol</dt><dd className="col-sm-8">{viewing.role_name || viewing.role_slug}</dd><dt className="col-sm-4">Estado</dt><dd className="col-sm-8">{viewing.enabled ? 'Activo' : 'Inactivo'}</dd></dl></div><div className="modal-footer"><button type="button" className="btn btn-light" onClick={() => setViewing(null)}>Cerrar</button><Link className="btn btn-primary" to={`/usuarios/${viewing.id}/editar`} onClick={() => setViewing(null)}>Editar</Link></div></div></div></div>}
    </>
  );
}
